import {
  AcademicTitle,
  CatalogueKind,
  UserRole,
  type ConfiguratorGroupKind,
  type ConfiguratorImageRuleMode,
} from '@prisma/client'

export {
  BankProvider,
  BankTransactionSide,
  BankTransactionStatus,
  CalculationBlockRole,
  CalculationBlockType,
  ChangeAction,
  ChangeActor,
  ChangeEntity,
  ConfiguratorAvailabilityConflict,
  ConfiguratorGroupKind,
  ConfiguratorImageRuleMode,
  ConfiguratorPriceMode,
  ConfiguratorPropertyType,
  ConfiguratorScope,
  ConfiguratorSelectionType,
  ConfiguratorStatus,
  ContractDirection,
  ContractKind,
  ContractStatus,
  DeductionBasis,
  DeductionSettlement,
  EmailStatus,
  EnergyLabel,
  FormulaSummaryScope,
  HouseElementMode,
  ImageCropMode,
  ImageFormat,
  InstallmentDueKind,
  InstallmentRole,
  InvoiceStatus,
  HouseElementScope,
  ProductSuggestionStatus,
  MatchStatus,
  McpUploadTarget,
  NotificationLevel,
  FloorType,
  NotificationStatus,
  PositionGate,
  PositionOrphanReason,
  PositionPriceAdjustment,
  PositionPriceKind,
  ProductClassificationKind,
  ProductPropertyType,
  ProjectStatus,
  PropertyFeature,
  PropertyMetric,
  PropertyType,
  CatalogueKind,
  CommentKind,
  RoomType,
  SubType,
  Unit,
  UserRole,
  UserType,
  Salutation,
  AcademicTitle,
  PhoneType,
} from '@prisma/client'

/**
 * The three places an answer can live, outermost first — the whole property,
 * one storey, one room.
 *
 * The order is the ladder itself: a question asked further down may depart
 * from the answer above it, never the other way round.
 *
 * Distinct from `ConfiguratorGroupKind`, which says what a *node* is: a rung
 * of kind FLOOR or ROOM opens the scope of the same name, and a plain GROUP
 * opens none — it keeps whatever scope it hangs in.
 */
export const SCOPE_LEVELS = ['OBJECT', 'FLOOR', 'ROOM'] as const

export type ScopeLevel = (typeof SCOPE_LEVELS)[number]

/** How far down the ladder a scope sits — its position in `SCOPE_LEVELS`, so
 *  the order is stated once. */
export const SCOPE_DEPTH = Object.fromEntries(
  SCOPE_LEVELS.map((level, depth) => [level, depth]),
) as Record<ScopeLevel, number>

/** The kinds of node, in the order the tree offers them. */
export const CONFIGURATOR_GROUP_KINDS = [
  'GROUP',
  'FLOOR',
  'ROOM',
] as const satisfies readonly ConfiguratorGroupKind[]

/** The scope a node of each kind opens. A plain group opens none. */
export const SCOPE_OF_KIND = {
  GROUP: null,
  FLOOR: 'FLOOR',
  ROOM: 'ROOM',
} as const satisfies Record<ConfiguratorGroupKind, ScopeLevel | null>

/**
 * The catalogue branches, in the order every surface offers them — which is
 * the order `schema.prisma` declares them in.
 *
 * `Object.values` of the enum, like `USER_ROLES` below, so a branch added to
 * the schema joins every tab rail without anyone remembering to. Written as a
 * list of its own, a fifth branch was simply absent from all of them — and
 * `isCatalogueKind` rejected its route.
 */
export const CATALOGUE_KINDS = Object.values(CatalogueKind)

/**
 * Every role, in the enum's order — the order every list of them is written
 * in, on either side of the wire.
 *
 * Four of the five are a ladder of privilege; ARCHITECT is not on it, so
 * nothing compares two roles by their position here.
 *
 * `Object.values` of the enum, so a member added to `schema.prisma` joins it
 * without anyone remembering to; taking it from a label table instead needs a
 * cast, which is what would let a typo there become a role nobody notices.
 */
export const USER_ROLES = Object.values(UserRole)

/** The academic titles, in the order a picker offers them. */
export const ACADEMIC_TITLES = Object.values(AcademicTitle)

/**
 * Narrows a route parameter to a catalogue branch.
 *
 * There is no global validation pipe, so an endpoint typed `CatalogueKind`
 * receives whatever the caller sent. Unchecked, an unknown value reaches a
 * `switch` that answers `undefined` for it — and `await undefined` resolves,
 * so a bulk delete reports every id as deleted without touching a row.
 */
export const isCatalogueKind = (value: string): value is CatalogueKind =>
  (CATALOGUE_KINDS as readonly string[]).includes(value)

/** What a measure's conditional pictures do to the gallery it already has,
 *  in the order an editor reads them. */
export const CONFIGURATOR_IMAGE_RULE_MODES = [
  'PREPEND',
  'APPEND',
  'REPLACE',
] as const satisfies readonly ConfiguratorImageRuleMode[]
